<style lang="scss">
@import '../../assets/scss/_variables';

.step-3 {
    .step-content {
        max-width: 1000px;
        margin-top: 46px;
        padding: 0px ($grid-size * 6);
    }

        .point-inputs {
            $arrow-move: 3px;
            position: relative;

            .fa-long-arrow-down {
                position: absolute;
                    top: 50%;
                    left: 1px;

                font-size: 32px;
                color: $color-black;
                transform: translate(-50%, #{5px - $arrow-move});
                transition: transform $transition-1;
            }

            .point-input-container:hover,
            .point-input-container.is-focused {
                & + .fa-long-arrow-down {
                    transform: translate(-50%, #{5px + $arrow-move});
                }
            }
        }

        .map-container {
            position: absolute;
            top: 0;
            right: 0;
            width: 40%;
            width: calc(100% - 1000px);
            height: 100%;
        }

            .vue-google-map-container,
            .overlay {
                width: 100%;
                height: 100%;
            }

            .overlay {
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                    top: 0;
                    left: 0;

                background: rgba($color-black, 0.8);
                transition: $transition-overlay;

                &.fade-enter,
                &.fade-leave-to {
                    opacity: 0;
                }
            }

                .google-loader {
                    width: 120px;
                }

        .step-btn-next {
            margin-top: $grid-size * 3;

            span {
                display: inline-block;
                margin-top: 6px;
                font-size: 22px;
                line-height: 1.1;
            }
        }

    @media (max-width: 1500px) {
        .step-content {
            max-width: 800px;
            padding: 0 ($grid-size * 4);
        }

            .map-container {
                width: calc(100% - 800px);
            }
    }

    @include media-smaller('lg') {
        .step-content {
            max-width: none;
            padding: 0 ($grid-size * 2);
        }

            .vue-autocomplete {
                margin-left: 9px;
            }

            .map-container {
                width: 100%;
                height: $grid-size * 10;
                max-height: 100vh;
                margin: $grid-size 0;
                position: relative;
            }

            .step-btn-next {
                margin-top: $grid-size * 2;
            }
    }

    @include media-smaller('md') {
        .step-content {
            padding: 0 $mobile-pad;
        }

            .point-inputs {
                .fa-long-arrow-down {
                    margin-top: -0.35em;
                }
            }

            .map-container {
                width: auto;
                height: 60vh;
                margin-left: -$mobile-pad;
                margin-right: -($mobile-pad + $step-border-width);
            }

                .google-loader {
                    width: 80px;
                }
    }
}
</style>

<template>
    <StepBase :index="index" :activeIndex="activeIndex" @set-active="$emit('set-active')">
        <template slot="subtitle">Enter <br> Your</template>
        <template slot="title">Route</template>

        <div class="step-content step-pad">
            <div class="point-inputs">
                <PointInput
                    point="a"
                    placeholder="Enter your start point"
                    @blur="route.origin = $event"
                ></PointInput>

                <PointInput
                    point="b"
                    placeholder="Enter your destination"
                    @blur="route.destination = $event"
                ></PointInput>

                <fa :icon="['fal', 'long-arrow-down']"></fa>
            </div>

            <transition name="fade">
                <InfoBox v-if="typeof error === 'string'" type="error">
                    <p slot="message">{{ error }}</p>
                </InfoBox>
            </transition>

            <div class="map-container">
                <GoogleMap
                    ref="map"
                    :options="mapOptions"
                    @initialized="mapObject = $event"
                ></GoogleMap>

                <transition name="fade">
                    <div v-if="mapLoading" class="overlay">
                        <Loader classList="accent-1"></Loader>
                    </div>
                </transition>
            </div>

            <button @click="proceed()" type="button" class="step-btn-next" :disabled="!canProceed">
                <span>Show Results</span>
            </button>
        </div>
    </StepBase>
</template>

<script>
import scroller from 'vue-scrollto';
import StepBase from './Base';
import PointInput from '../entities/PointInput';
import InfoBox from '../entities/InfoBox';
import mapStyles from '../../assets/js/map-styles';
import GoogleMap from '../entities/GoogleMap';
import Loader from '@/components/entities/Loader';

export default {
    extends: StepBase,
    components: {StepBase, PointInput, InfoBox, GoogleMap, Loader},
    data () {
        return {
            status: null,
            placeStatus: null,
            route: {
                origin: null,
                destination: null
            },
            mapLoading: false,
            mapObject: null,
            mapOptions: {
                styles: mapStyles,
                disableDefaultUI: true,
                gestureHandling: 'cooperative',
                zoom: 3,
                center: {
                    lat: 10,
                    lng: -20
                }
            },
            directionsService: new window.google.maps.DirectionsService(),
            directionsRenderer: new window.google.maps.DirectionsRenderer({
                suppressMarkers: true,
                polylineOptions: new window.google.maps.Polyline({
                    strokeColor: '#FFC735',
                    strokeOpacity: 1.0,
                    strokeWeight: 5
                })
            })
        };
    },
    computed: {
        canProceed () {
            return (this.status === 'OK') && !this.error;
        },
        error () {
            var hasOrigin = !!this.route.origin,
                hasDestination = !!this.route.destination;

            if (!hasOrigin || !hasDestination) {
                return true;
            }

            var originId = this.route.origin.place_id || null,
                destinationId = this.route.destination.place_id || null;

            if (!originId || !destinationId) {
                return 'We need a valid location, so please use the location dropdown and pick an item from it.';
            }

            if (originId && originId === destinationId) {
                return 'Pick two different locations, please...';
            }

            if (typeof this.status === 'string' && this.status !== 'OK') {
                switch (this.status) {
                case 'INVALID_REQUEST': return 'Error while requesting route.';
                case 'NOT_FOUND': return 'One of the locations couldn\'t be geocoded.';
                case 'OVER_QUERY_LIMIT': return 'Too frequent requests.';
                case 'REQUEST_DENIED': return 'No access to the directions service';
                case 'UNKNOWN_ERROR': return 'An unknown error occurred.';
                case 'ZERO_RESULTS': return 'No route could be found between the origin and destination. Please, pick different locations.';
                }
            }

            return null;
        }
    },
    methods: {
        resetMap () {
            this.directionsRenderer.setMap(null);
            this.mapObject.setCenter(this.mapOptions.center);
            this.mapObject.setZoom(this.mapOptions.zoom);
        },
        updateRoute () {
            var originId = (this.route.origin && this.route.origin.place_id),
                destinationId = (this.route.destination && this.route.destination.place_id);

            if (!originId || !destinationId || originId === destinationId) {
                return;
            }

            this.status = null;
            this.mapLoading = true;

            this.directionsService.route({
                origin: {
                    placeId: originId
                },
                destination: {
                    placeId: destinationId
                },
                travelMode: 'DRIVING'
            }, (response, status) => {
                if (status === 'OK') {
                    this.mapObject.fitBounds(response.routes[0].bounds);
                    this.directionsRenderer.setMap(this.mapObject);
                    this.directionsRenderer.setDirections(response);

                    if (!this.status) {
                        this.emphasizeMap(); // empasize only the first change
                    }
                }

                this.status = status;
                this.mapLoading = false;
            });
        },
        emphasizeMap () {
            if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
            }

            if (this.$refs.map && this.$store.getters.viewport < 2) {
                scroller.scrollTo(this.$refs.map.$el, {
                    duration: 600,
                    offset: 2
                });
            }
        },
        getFormData () {
            return {
                route: this.route
            };
        }
    },
    watch: {
        route: {
            deep: true,
            handler () {
                this.updateRoute();
            }
        },
        status (newValue) {
            if (newValue !== 'OK') {
                this.resetMap();
            }
        }
    }
};
</script>
